import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import {
  GroupHeader,
  GroupItems,
} from '../../../../shared/autocomplete-groups-ui';
import { BulkImportType } from '../../../../../types';
import { isAbleToSeeUIElement } from '../../../../utils/roles/client-roles';
import dayjs from 'dayjs';
import { BulkOpsFormValues } from './create-bulk-op-dialog';
import ScopesAlert from '../../../../shared/scopes-alert';
import { SectionHeader } from '../../../../shared/section-header';
import { FormSectionHeader } from '../../../../shared/form-section-header';
import AvatarChip from '../../../../shared/avatar-chip';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Grow from '@mui/material/Grow';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

interface StepBasicInformationProps {
  control: Control<BulkOpsFormValues>;
  setValue: UseFormSetValue<BulkOpsFormValues>;
  errors: FieldErrors<BulkOpsFormValues>;
  bulkImportTypes: BulkImportType[];
  setSelectedBulkImportType: Dispatch<SetStateAction<BulkImportType | null>>;
  selectedBulkImportType: BulkImportType | null;
  toolsRoles: string[];
  setSpreadSheetName: Dispatch<SetStateAction<string>>;
  groupDeletionChecked: boolean;
  setGroupDeletionChecked: Dispatch<SetStateAction<boolean>>;
  showDeletionError: boolean;
  setShowDeletionError: Dispatch<SetStateAction<boolean>>;
  apiAccessGranted: boolean;
  checkingApiAccess: boolean;
  cloudIdentityBulkOpTypes: string[];
  driveLabelsAccessGranted: boolean;
  checkingDriveLabelsAccess: boolean;
  driveLabelsBulkOpTypes: string[];
  watch: UseFormWatch<BulkOpsFormValues>;
  clearErrors: UseFormClearErrors<BulkOpsFormValues>;
  setStepCompleted: Dispatch<SetStateAction<boolean[]>>;
  isNewUi?: boolean;
}

export default function StepBasicInformation({
  control,
  errors,
  bulkImportTypes,
  setSelectedBulkImportType,
  selectedBulkImportType,
  toolsRoles,
  setValue,
  setSpreadSheetName,
  groupDeletionChecked,
  setGroupDeletionChecked,
  showDeletionError,
  setShowDeletionError,
  apiAccessGranted,
  checkingApiAccess,
  cloudIdentityBulkOpTypes,
  driveLabelsAccessGranted,
  checkingDriveLabelsAccess,
  driveLabelsBulkOpTypes,
  watch,
  clearErrors,
  setStepCompleted,
  isNewUi = false,
}: StepBasicInformationProps) {
  const theme = useTheme();
  const fieldWidth = isNewUi ? 438 : 400;
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const bulkImportTypeOptions = bulkImportTypes.map((type) => {
    if (
      type.name === 'abandonedCalendarEvents' ||
      type.name === 'calendarPermissionSettings' ||
      type.name === 'removeCalendarRoom' ||
      type.name === 'calendarEventsImport'
    ) {
      return {
        ...type,
        groupType: 'Calendars',
      };
    } else if (
      type.name === 'driveTransfer' ||
      type.name === 'removeSharesFromUser' ||
      type.name === 'removeUsersShares' ||
      type.name === 'labelDriveDocs'
    ) {
      return {
        ...type,
        groupType: 'Drive',
      };
    } else if (
      type.name === 'clearUsersFromGroup' ||
      type.name === 'groupDeletion' ||
      type.name === 'groupImport' ||
      type.name === 'groupRemoval' ||
      type.name === 'groupMemberSettingsUpdate'
    ) {
      return {
        ...type,
        groupType: 'Groups',
      };
    } else {
      return {
        ...type,
        groupType: 'Users',
      };
    }
  });
  const nextDay = useRef(dayjs().add(1, 'day'));
  const dayAfter = useRef(dayjs().add(2, 'day'));
  const autocompleteKey = useRef(new Date().getTime().toString());
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const emailsToNotify = watch('notifyEmails');
  function optionTooltipText(option: BulkImportType) {
    if (!isAbleToSeeUIElement(toolsRoles, option.role)) {
      return 'You do not have the required permission to select this bulk operation type.';
    } else {
      return option.description;
    }
  }

  function optionDisable(option: BulkImportType) {
    if (!isAbleToSeeUIElement(toolsRoles, option.role)) {
      return true;
    } else {
      return false;
    }
  }

  const isCloudIdentityBulkOpTypesSelected =
    selectedBulkImportType?.name !== '' &&
    selectedBulkImportType?.name !== undefined &&
    cloudIdentityBulkOpTypes.includes(selectedBulkImportType?.name);

  const isDriveLabelsBulkOpTypesSelected =
    selectedBulkImportType?.name !== '' &&
    selectedBulkImportType?.name !== undefined &&
    driveLabelsBulkOpTypes.includes(selectedBulkImportType?.name);

  return (
    <Stack gap={2} width={'100%'}>
      {isNewUi ? (
        <FormSectionHeader
          descriptionVariant="body2"
          description="Please provide a name, optionally provide a description, and choose your bulk operation type."
        >
          Basic Information
        </FormSectionHeader>
      ) : (
        <SectionHeader
          description="Please provide a name, optionally provide a description, and choose your
        bulk operation type."
        >
          Basic Information
        </SectionHeader>
      )}
      <Stack alignItems="flex-start" gap={2}>
        <Stack flexDirection="row" gap={isNewUi ? 3 : 4}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                required
                inputProps={{
                  id: 'name',
                }}
                InputLabelProps={{
                  htmlFor: 'name',
                }}
                label="Name"
                sx={{ width: fieldWidth }}
                {...field}
                error={!!errors.name}
                helperText={errors?.name?.message || ' '}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'description',
                }}
                InputLabelProps={{
                  htmlFor: 'description',
                }}
                label="Description"
                sx={{ width: fieldWidth }}
              />
            )}
          />
        </Stack>
        <Stack flexDirection={'row'} gap={4} justifyContent="space-between">
          <Controller
            name="bulkImport"
            control={control}
            render={({ field }) => (
              <Autocomplete
                id="operationType"
                autoComplete
                autoHighlight
                options={bulkImportTypeOptions.sort((a, b) =>
                  a.groupType.localeCompare(b.groupType)
                )}
                sx={{ width: fieldWidth }}
                getOptionLabel={(option) => option.niceName || ''}
                value={
                  bulkImportTypeOptions.find(
                    (type) => type.name === selectedBulkImportType?.name
                  ) || null
                }
                groupBy={(option) => option.groupType}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <GroupHeader>{params.group}</GroupHeader>
                    <GroupItems>{params.children}</GroupItems>
                  </li>
                )}
                onChange={(_event, value) => {
                  if (value) {
                    field.onChange(value!.name);
                    setSelectedBulkImportType(value);
                    if (value.usesSpreadsheet || value.usesFolder) {
                      setValue('spreadsheetId', '');
                    } else {
                      setValue('spreadsheetId', null);
                    }
                    setValue('vacationResponderData', null);
                    setSpreadSheetName('');
                    setGroupDeletionChecked(false);
                    setShowDeletionError(false);
                    setValue('includeNestedGroups', null);
                    setValue('createGroups', false);
                    setValue('resourceIds', null);
                    setValue('startDate', 0);
                    setValue('endDate', 0);
                    setValue('path', null);
                    setValue('sourceType', null);
                    setValue('sources', []);
                    setValue('allSource', false);
                    if (
                      value.name === 'clearUsersFromGroup' ||
                      value.name === 'groupDeletion'
                    ) {
                      setValue('sourceType', 'Group');
                      setValue('path', null);
                      setValue('columns', null);
                      setValue('spreadsheetId', null);
                    } else if (
                      value.name === 'personalContactRemoval' ||
                      value.name === 'groupRemoval' ||
                      value.name === 'forceSignOut' ||
                      value.name === 'forcePasswordReset'
                    ) {
                      setValue('sourceType', 'User');
                      setValue('path', null);
                      setValue('columns', null);
                      setValue('spreadsheetId', null);
                    } else if (
                      value.name === 'calendarPermissionSettings' ||
                      value.name === 'userImport' ||
                      value.name === 'signatureTemplateBlackListStatus' ||
                      value.name === 'labelDriveDocs' ||
                      value.name === 'removeUsersShares' ||
                      value.name === 'removeSharesFromUser'
                    ) {
                      setValue('columns', value.columns);
                      setValue('sourceType', 'User');
                      setValue('path', 'guideMe');
                      setValue('spreadsheetId', '');
                    } else if (value.name === 'gPanelRoleImport') {
                      setValue('columns', value.columns);
                      setValue('sourceType', 'gPanel_Role');
                      setValue('path', 'guideMe');
                      setValue('spreadsheetId', '');
                    } else if (
                      value.name === 'groupImport' ||
                      value.name === 'groupMemberSettingsUpdate'
                    ) {
                      setValue('path', 'guideMe');
                      setValue('sourceType', 'Group');
                      setValue('columns', value.columns);
                      setValue('includeNestedGroups', []);
                    } else if (value.name === 'sharedContactImport') {
                      setValue('path', 'guideMe');
                      setValue('columns', value.columns);
                      setValue('sourceType', 'User');
                      setValue('spreadsheetId', '');
                    } else if (value.name === 'removeCalendarRoom') {
                      setValue('resourceIds', []);
                      setValue('startDate', today.getTime());
                      setValue('endDate', tomorrow.getTime());
                      setValue('path', null);
                      setValue('columns', null);
                      setValue('spreadsheetId', null);
                    } else if (value.name === 'usersVacationResponder') {
                      setValue('startDate', today.getTime());
                      setValue('endDate', tomorrow.getTime());
                      setValue('sourceType', 'User');
                      setValue('path', null);
                      setValue('spreadsheetId', null);
                      setValue('columns', null);
                      setValue('vacationResponderData', {
                        enableAutoReply: true,
                        restrictToContacts: true,
                        restrictToDomain: true,
                        responseSubject: '',
                        responseBodyHtml: '',
                        startTime: nextDay.current.toDate().getTime(),
                        endTime: dayAfter.current.toDate().getTime(),
                      });
                    }
                  } else {
                    field.onChange('');
                    setSelectedBulkImportType(null);
                    setValue('sourceType', null);
                    setValue('spreadsheetId', null);
                    setValue('vacationResponderData', null);
                    setSpreadSheetName('');
                    setValue('path', null);
                    setValue('columns', null);
                    setGroupDeletionChecked(false);
                    setShowDeletionError(false);
                    setValue('includeNestedGroups', null);
                    setValue('createGroups', false);
                    setValue('resourceIds', null);
                    setValue('startDate', 0);
                    setValue('endDate', 0);
                  }
                  clearErrors();
                  setStepCompleted([false, false, false, false, false, false]);
                }}
                isOptionEqualToValue={(option, value) =>
                  option.name === value?.name
                }
                getOptionDisabled={(option) => {
                  return !!optionDisable(option);
                }}
                renderOption={(props, option) => (
                  <Tooltip
                    key={option.name}
                    title={optionTooltipText(option)}
                    placement="right"
                    arrow
                  >
                    <span>
                      <ListItem {...props} key={option.name}>
                        {option.niceName}
                      </ListItem>
                    </span>
                  </Tooltip>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Bulk Import Type"
                    required
                    error={!!errors.bulkImport}
                    helperText={
                      errors?.bulkImport?.message || (isNewUi ? undefined : ' ')
                    }
                  />
                )}
              />
            )}
          />
          <Box width={400}>
            {checkingApiAccess && (
              <Stack direction="row" gap={1} pt={1.5}>
                <Typography variant="body2" color="text.secondary">
                  Checking data access...
                </Typography>
                <CircularProgress size={20} />
              </Stack>
            )}
          </Box>
        </Stack>

        {selectedBulkImportType && isNewUi ? (
          <Box
            sx={{
              width: fieldWidth,
              mt: -1,
              px: 2,
              py: 1,
              borderRadius: 1,
              backgroundColor: theme.palette.semantic.surface.bg2,
            }}
          >
            <Typography variant="body2">
              {selectedBulkImportType.description}
            </Typography>
          </Box>
        ) : selectedBulkImportType ? (
          <Grid size={{ xs: 12 }}>
            <Typography>{selectedBulkImportType.description}</Typography>
          </Grid>
        ) : null}
        {selectedBulkImportType?.name === 'groupDeletion' ? (
          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={groupDeletionChecked}
                  onChange={() => {
                    if (showDeletionError) {
                      setShowDeletionError(false);
                    }
                    setGroupDeletionChecked(!groupDeletionChecked);
                  }}
                />
              }
              label="I understand that bulk deletion of groups is a permanent action and cannot be undone."
            />
          </Grid>
        ) : null}
      </Stack>
      {showDeletionError ? (
        <Grow in={true}>
          <Alert severity="error" sx={{ width: 'fit-content' }}>
            {
              'Please confirm that you understand this is a permanent action and cannot be undone.'
            }
          </Alert>
        </Grow>
      ) : null}
      {isCloudIdentityBulkOpTypesSelected &&
        !apiAccessGranted &&
        !checkingApiAccess && (
          <ScopesAlert
            affectedColumns={['Created Date']}
            apiKey={'cloudIdentity'}
            isReportsOrBulkOps
          />
        )}
      {isDriveLabelsBulkOpTypesSelected &&
        !driveLabelsAccessGranted &&
        !checkingDriveLabelsAccess && (
          <ScopesAlert
            apiKey={'driveLabels'}
            additionalText="This bulk operation cannot resolve or apply labels until access is granted."
          />
        )}

      {isNewUi ? (
        <>
          <Divider
            sx={{ my: 2, borderColor: theme.palette.semantic.outline.light }}
          />
          <FormSectionHeader
            descriptionVariant="body2"
            description="Optionally provide any specific users/groups that you would like to send an email to upon this bulk operations completion."
          >
            Email Notifications (optional)
          </FormSectionHeader>
        </>
      ) : (
        <SectionHeader
          sx={{ mt: 2 }}
          description="Optionally provide any specific users/groups that you would like to send an
        email to upon this bulk operations completion."
        >
          Email Notifications
        </SectionHeader>
      )}
      <Stack>
        <Stack gap={1}>
          <Stack flexDirection="row" gap={1}>
            <Controller
              name={'notifyEmails'}
              control={control}
              render={() => (
                <Box sx={{ width: 400 }}>
                  <SharedAutocomplete
                    sources={['users', 'groups']}
                    autocompleteValue={autoCompleteResponse}
                    setAutoCompleteValue={(val) => {
                      setAutoCompleteResponse(val);
                      if (val && !emailsToNotify?.includes(val)) {
                        const updatedUsers = [...(emailsToNotify || []), val];
                        setValue('notifyEmails', updatedUsers, {
                          shouldDirty: true,
                        });
                        autocompleteKey.current = new Date()
                          .getTime()
                          .toString();
                        setAutoCompleteResponse('');
                      }
                    }}
                  />
                </Box>
              )}
            />
          </Stack>
          <Stack
            flexDirection="row"
            gap={1}
            flexWrap="wrap"
            sx={{ mt: 1, mb: 2 }}
          >
            {emailsToNotify && emailsToNotify.length > 0
              ? emailsToNotify.map((user) => {
                  return (
                    <AvatarChip
                      key={user}
                      label={user}
                      type={'User'}
                      email={user}
                      onDelete={() => {
                        setValue(
                          'notifyEmails',
                          emailsToNotify.filter(
                            (notifiedUser) => notifiedUser !== user
                          )
                        );
                      }}
                    />
                  );
                })
              : null}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
